import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../componentes/Plantilla/Plantilla';
import { DataContext } from '../../contexto/ContextoDatos';
import ProductCard from '../../componentes/Secciones/Productos/TarjetaProducto';
import PanelFiltros from '../../componentes/Catalogo/PanelFiltros';
import catalogStyles from '../../componentes/Catalogo/Catalogo.module.css';
import styles from './PaginaProductos.module.css';
import logoSeima from '../../recursos/imagenes/logo.webp';
import Breadcrumbs from '../../componentes/UI/Breadcrumbs';

export const ProductsIndex = () => {
  const { products, categories } = useContext(DataContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const getArrayParam = (key) => {
    const val = searchParams.get(key);
    return val ? val.split('|') : [];
  };

  const activeFilters = {
    categorias: getArrayParam('categorias'),
    marcas: getArrayParam('marcas'),
    sectores: getArrayParam('sectores'),
    especialidades: getArrayParam('especialidades'),
    disponibilidad: getArrayParam('disponibilidad'),
  };

  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem('catalog_page');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = sessionStorage.getItem('catalog_itemsPerPage');
    return saved ? parseInt(saved, 10) : 9;
  });

  useEffect(() => {
    sessionStorage.setItem('catalog_page', currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    sessionStorage.setItem('catalog_itemsPerPage', itemsPerPage.toString());
  }, [itemsPerPage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const previousParams = useRef(searchParams.toString());
  useEffect(() => {
    const currentParams = searchParams.toString();
    if (previousParams.current !== currentParams) {
      setCurrentPage(1);
      previousParams.current = currentParams;
    }
  }, [searchParams]);



  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchCat = activeFilters.categorias.length === 0 || activeFilters.categorias.includes(p.category_slug);
      const matchMarca = activeFilters.marcas.length === 0 || activeFilters.marcas.includes(p.filters?.brand);
      const matchSector = activeFilters.sectores.length === 0 || activeFilters.sectores.includes(p.filters?.sector);
      const matchDisp = activeFilters.disponibilidad.length === 0 || activeFilters.disponibilidad.includes(p.filters?.availability);
      const matchEspec = activeFilters.especialidades.length === 0 || p.filters?.specialties.some(s => activeFilters.especialidades.includes(s));
      return matchCat && matchMarca && matchSector && matchDisp && matchEspec;
    });
  }, [
    products, 
    activeFilters.categorias, 
    activeFilters.marcas, 
    activeFilters.sectores, 
    activeFilters.disponibilidad, 
    activeFilters.especialidades
  ]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const firstItemIndex = filteredProducts.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const lastItemIndex = Math.min(currentPage * itemsPerPage, filteredProducts.length);

  const handleFilterChange = (group, newValues) => {
    const newParams = new URLSearchParams(searchParams);
    if (newValues && newValues.length > 0) {
      newParams.set(group, newValues.join('|'));
    } else {
      newParams.delete(group);
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Smooth scroll back to top of the list
      const listElement = document.getElementById('product-list');
      if (listElement) {
        const y = listElement.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };

  const getPaginationRange = () => {
    const totalPageNumbers = 7;
    if (totalPages <= totalPageNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const leftSiblingIndex = Math.max(currentPage - 1, 1);
    const rightSiblingIndex = Math.min(currentPage + 1, totalPages);

    const showLeftDots = leftSiblingIndex > 2;
    const showRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!showLeftDots && showRightDots) {
      const leftItemCount = 4;
      const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, '...', totalPages];
    }

    if (showLeftDots && !showRightDots) {
      const rightItemCount = 4;
      const rightRange = Array.from({ length: rightItemCount }, (_, i) => totalPages - rightItemCount + 1 + i);
      return [firstPageIndex, '...', ...rightRange];
    }

    if (showLeftDots && showRightDots) {
      const middleRange = [leftSiblingIndex, currentPage, rightSiblingIndex];
      return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
    }
  };

  const totalActiveFilters = Object.values(activeFilters).reduce((acc, curr) => acc + curr.length, 0);
  let categoryText = '';
  if (totalActiveFilters === 1) categoryText = `con 1 filtro activo`;
  else if (totalActiveFilters > 1) categoryText = `con ${totalActiveFilters} filtros activos`;

  return (
    <Layout>
      <section className={styles.hero}>
        <div className="container">
          <div className={styles.heroInner}>
            <Breadcrumbs paths={[{ label: 'Inicio', to: '/' }, { label: 'Productos' }]} />
            <div className={styles.tituloBloque}>
              <img src={logoSeima} alt="SEIMA" className={styles.logoHero} />
              <h1 className={styles.tituloHero}>
                Equipos <span className={styles.acento}>Industriales</span>
              </h1>
            </div>
            <div className={styles.lineaHero} />
            <p className={styles.subHero}>
              Stock permanente de maquinaria de alta eficiencia para lavandería y sistemas auxiliares.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.listingSection} id="product-list">
        <div className="container">
          <div className={catalogStyles.catalogLayout}>
            <PanelFiltros
              products={products}
              categories={categories}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              onClearFilters={() => setSearchParams({})}
            />

            <div>
              <div className={styles.listingHeader}>
                <p className={styles.listingCount}>
                  {filteredProducts.length > 0 ? (
                    <>Mostrando <strong>{firstItemIndex}–{lastItemIndex}</strong> de <strong>{filteredProducts.length}</strong> equipos {categoryText}</>
                  ) : (
                    <>No se encontraron equipos</>
                  )}
                </p>
                <div className={styles.perPageSelector}>
                  <label htmlFor="perPage">Mostrar:</label>
                  <select
                    id="perPage"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={9}>9 por página</option>
                    <option value={12}>12 por página</option>
                    <option value={24}>24 por página</option>
                    <option value={48}>48 por página</option>
                  </select>
                </div>
              </div>

              {paginatedProducts.length > 0 ? (
                <>
                  <div key={currentPage} className={`grid-3 ${styles.gridContainer}`}>
                    {paginatedProducts.map(product => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button 
                        className={styles.pageBtn} 
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        title="Ir al principio"
                      >
                        «
                      </button>
                      <button 
                        className={styles.pageBtn} 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        title="Anterior"
                      >
                        ‹
                      </button>
                      
                      {getPaginationRange().map((page, index) => {
                        if (page === '...') {
                          return <span key={`dots-${index}`} className={styles.pageDots}>...</span>;
                        }
                        return (
                          <button
                            key={page}
                            className={`${styles.pageBtn} ${currentPage === page ? styles.active : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button 
                        className={styles.pageBtn} 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        title="Siguiente"
                      >
                        ›
                      </button>
                      <button 
                        className={styles.pageBtn} 
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        title="Ir al final"
                      >
                        »
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.emptyState}>
                  <p>No hay equipos en esta categoría.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
export default ProductsIndex;

